import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import { GripVertical } from "lucide-react";
import { useEffect, useId, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  ROOM_RAIL_SORTS,
  type RoomRailPreferences,
  type RoomRailSort,
} from "~/neucleos/roomRailArrangement";
import { cn } from "~/lib/utils";
import { resolveRoomRailDragDestination } from "./RoomRailSection.logic";
import { Popover, PopoverPopup, PopoverTrigger } from "./ui/popover";
import { dropConversationOnComposer, moveConversationDrag } from "./chat/conversationDrag";

export function RoomRailSortSelect({
  label,
  value,
  onChange,
  inherit = false,
  flat = false,
}: {
  label: string;
  value: RoomRailSort | "inherit";
  onChange: (sort: RoomRailSort | "inherit") => void;
  inherit?: boolean;
  flat?: boolean;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5 text-xs">
      {label}
      <select
        id={id}
        aria-label={label}
        className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        value={value}
        onChange={(event) => onChange(event.target.value as RoomRailSort | "inherit")}
      >
        {inherit ? <option value="inherit">Use default</option> : null}
        {Object.entries(ROOM_RAIL_SORTS)
          .filter(([sort]) => !flat || sort !== "manual")
          .map(([sort, title]) => (
            <option value={sort} key={sort}>
              {title}
            </option>
          ))}
      </select>
    </label>
  );
}
export function RoomRailArrangementControls({
  preferences,
  onChange,
  onFoldAll,
  showRoomFolds = true,
  allFolded,
  compact,
  onCompactChange,
  onTidy,
}: {
  /** Can 80: open Tidy up for every room. */
  onTidy?: (() => void) | undefined;
  preferences: RoomRailPreferences;
  onChange: (next: RoomRailPreferences) => void;
  onFoldAll: () => void;
  showRoomFolds?: boolean;
  allFolded: boolean;
  /** UP13: compact rows (36px with status, PR badge and time); absent hides the toggle. */
  compact?: boolean;
  onCompactChange?: (next: boolean) => void;
}) {
  return (
    <div
      className="flex flex-col gap-3 border-b border-border px-2 pt-2 pb-3"
      data-room-rail-arrange=""
    >
      <span className="text-xs font-medium">Arrange</span>
      <div className="flex gap-1 rounded-md bg-muted p-1" aria-label="Session grouping">
        {(
          [
            ["rooms", "By Room"],
            ["sessions", "All sessions"],
          ] as const
        ).map(([view, label]) => (
          <button
            type="button"
            key={view}
            aria-pressed={preferences.view === view}
            className="flex-1 rounded px-2 py-1 text-xs aria-pressed:bg-background aria-pressed:text-foreground hover:bg-accent"
            onClick={() => onChange({ ...preferences, view })}
          >
            {label}
          </button>
        ))}
      </div>
      {preferences.view === "rooms" ? (
        <RoomRailSortSelect
          label="Room order"
          value={preferences.roomSort}
          onChange={(sort) => onChange({ ...preferences, roomSort: sort as RoomRailSort })}
        />
      ) : null}
      <RoomRailSortSelect
        label={preferences.view === "rooms" ? "Default session order" : "Session order"}
        value={preferences.view === "rooms" ? preferences.sessionSort : preferences.flatSort}
        flat={preferences.view === "sessions"}
        onChange={(sort) =>
          onChange(
            preferences.view === "rooms"
              ? { ...preferences, sessionSort: sort as RoomRailSort }
              : { ...preferences, flatSort: sort as RoomRailPreferences["flatSort"] },
          )
        }
      />
      <label className="flex flex-col gap-1.5 text-xs">
        Show
        <select
          aria-label="Filter sessions"
          value={preferences.filter}
          onChange={(event) =>
            onChange({
              ...preferences,
              filter: event.target.value as RoomRailPreferences["filter"],
            })
          }
          className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="all">All open sessions</option>
          <option value="needs">Needs you</option>
          <option value="working">Working</option>
        </select>
      </label>
      <label className="flex flex-col gap-1.5 text-xs">
        Fold away
        <select
          aria-label="Fold away quiet sessions"
          data-room-rail-quiet-days={preferences.quietDays}
          value={preferences.quietDays}
          onChange={(event) =>
            onChange({
              ...preferences,
              quietDays: Number(event.target.value) as RoomRailPreferences["quietDays"],
            })
          }
          className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value={0}>Keep every session in view</option>
          <option value={1}>Sessions quiet for a day</option>
          <option value={3}>Sessions quiet for 3 days</option>
          <option value={7}>Sessions quiet for a week</option>
          <option value={14}>Sessions quiet for 2 weeks</option>
        </select>
      </label>
      {showRoomFolds && preferences.view === "rooms" ? (
        <button
          type="button"
          className="rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
          onClick={onFoldAll}
        >
          {allFolded ? "Expand all Rooms" : "Collapse all Rooms"}
        </button>
      ) : null}
      {onCompactChange ? (
        <button
          type="button"
          aria-pressed={compact === true}
          data-room-rail-compact-toggle=""
          className="rounded px-2 py-1.5 text-left text-xs hover:bg-accent aria-pressed:bg-background aria-pressed:text-foreground"
          onClick={() => onCompactChange(!compact)}
        >
          {compact ? "✓ Compact rows" : "Compact rows"}
        </button>
      ) : null}
      {onTidy ? (
        <button
          type="button"
          className="rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
          data-room-rail-tidy=""
          onClick={onTidy}
        >
          Tidy up…
        </button>
      ) : null}
      <span className="text-[10px] text-muted-foreground">
        Your arrangement · saved on this device
      </span>
    </div>
  );
}
export interface RoomRailDragData {
  kind: "room" | "session" | "pin" | "launcher" | "context-view";
  id: string;
  parentId: string;
  label: string;
}
function dragData(value: unknown): RoomRailDragData | undefined {
  return value as RoomRailDragData | undefined;
}
function isAfter(event: DragEndEvent | DragOverEvent): boolean {
  const initial = event.activatorEvent;
  const y =
    "clientY" in initial
      ? Number(initial.clientY) + event.delta.y
      : (event.active.rect.current.translated?.top ?? 0);
  return event.over !== null && y > event.over.rect.top + event.over.rect.height / 2;
}
/**
 * A row's reach: the grip stands outside its row (14px to the left or 10px to
 * the right), and chips leave a gap between rows, so a drag that keeps to the
 * grip column never enters a row's own box. The pointer counts as over a row
 * when it is within the row's band widened by the grip's reach and half the
 * chip gap; dropping well away from the list still lands nowhere.
 */
const ROW_REACH_PX = { x: 24, y: 8 };
/**
 * Whether a drop of `source` on `candidate` means anything: the same list
 * (only the shared pin list spans rooms), or — UP13, upstream #9731/#9750
 * re-created — a session crossing into the pinned block, or a pin crossing
 * back among a room's sessions.
 */
export function roomRailDropAccepts(
  source: Pick<RoomRailDragData, "kind" | "parentId"> | undefined,
  candidate: Pick<RoomRailDragData, "kind" | "parentId"> | undefined,
): boolean {
  if (source === undefined || candidate === undefined) return false;
  if (candidate.kind === source.kind) return candidate.parentId === source.parentId;
  return resolveRoomRailDragDestination(source, candidate) !== null;
}
/** Pointer must land inside the same list, or across the pinned/room seam. */
export const roomRailCollisionDetection: CollisionDetection = (args) => {
  const source = dragData(args.active.data.current);
  const droppableContainers = args.droppableContainers.filter((container) =>
    roomRailDropAccepts(source, dragData(container.data.current)),
  );
  const droppableRects = new Map(
    droppableContainers.flatMap((container) => {
      const rect = args.droppableRects.get(container.id);
      if (!rect) return [];
      return [
        [
          container.id,
          {
            ...rect,
            top: rect.top - ROW_REACH_PX.y,
            bottom: rect.bottom + ROW_REACH_PX.y,
            left: rect.left - ROW_REACH_PX.x,
            right: rect.right + ROW_REACH_PX.x,
            width: rect.width + ROW_REACH_PX.x * 2,
            height: rect.height + ROW_REACH_PX.y * 2,
          },
        ] as const,
      ];
    }),
  );
  return pointerWithin({ ...args, droppableContainers, droppableRects });
};
export function RoomRailDragArea({
  children,
  onMove,
  onCross,
  onDragging,
  conversationOf,
  className,
  contentClassName,
}: {
  children: ReactNode;
  /** Replaces the area's own `flex-none`, e.g. to fill a dock column. */
  className?: string | undefined;
  contentClassName?: string | undefined;
  /**
   * Cleo Can 53: the conversation a dragged row stands for. A sort drag that
   * ends over a composer adds that conversation there instead of reordering.
   */
  conversationOf?:
    | ((item: RoomRailDragData) => { readonly threadId: string; readonly title: string } | null)
    | undefined;
  onMove: (item: RoomRailDragData, overId: string, after: boolean) => void;
  /** UP13: a drop across the pinned/room seam; absent keeps drags within one list. */
  onCross?: ((item: RoomRailDragData, over: RoomRailDragData, after: boolean) => void) | undefined;
  onDragging: (dragging: boolean) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [active, setActive] = useState<RoomRailDragData | null>(null);
  const [target, setTarget] = useState<{
    id: string;
    after: boolean;
    over: RoomRailDragData | undefined;
  } | null>(null);
  // The cue the dragged row wears while crossing sections: "→ Pinned" or "→ Room".
  const destination =
    active && target?.over && onCross ? resolveRoomRailDragDestination(active, target.over) : null;
  const cue = destination !== null && destination.kind !== "reorder" ? destination.label : null;
  // The overlay portals into the body only once mounted on the client: the
  // server renderer refuses portals, and `document` alone does not tell the
  // two apart (jsdom defines it while rendering static markup).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const end = () => {
    setActive(null);
    setTarget(null);
    onDragging(false);
    moveConversationDrag(null);
  };
  const pointerOf = (event: DragEndEvent | DragOverEvent) => {
    const initial = event.activatorEvent;
    return "clientX" in initial && "clientY" in initial
      ? { x: Number(initial.clientX) + event.delta.x, y: Number(initial.clientY) + event.delta.y }
      : null;
  };
  const conversationFor = (item: RoomRailDragData | undefined) =>
    conversationOf && item && (item.kind === "session" || item.kind === "pin")
      ? conversationOf(item)
      : null;
  return (
    <div className={cn("w-full min-w-0", className ?? "flex-none")}>
      <DndContext
        sensors={sensors}
        collisionDetection={roomRailCollisionDetection}
        onDragStart={(event) => {
          setActive(dragData(event.active.data.current) ?? null);
          onDragging(true);
        }}
        onDragMove={(event) => {
          if (conversationFor(dragData(event.active.data.current))) {
            moveConversationDrag(pointerOf(event));
          }
          setTarget(
            event.over
              ? {
                  id: String(event.over.id),
                  after: isAfter(event),
                  over: dragData(event.over.data.current),
                }
              : null,
          );
        }}
        onDragOver={(event) =>
          setTarget(
            event.over
              ? {
                  id: String(event.over.id),
                  after: isAfter(event),
                  over: dragData(event.over.data.current),
                }
              : null,
          )
        }
        onDragEnd={(event) => {
          const source = dragData(event.active.data.current),
            over = dragData(event.over?.data.current);
          // Cleo Can 53: ending over a composer names the conversation there.
          const conversation = conversationFor(source);
          const point = pointerOf(event);
          if (conversation && point && dropConversationOnComposer(point, conversation)) {
            end();
            return;
          }
          if (source && over && source.kind === over.kind && source.parentId === over.parentId)
            onMove(source, over.id, isAfter(event));
          else if (source && over && onCross && roomRailDropAccepts(source, over))
            onCross(source, over, isAfter(event));
          end();
        }}
        onDragCancel={end}
        accessibility={{
          announcements: {
            onDragStart: () => "Reordering. The current session stays selected.",
            onDragOver: ({ active: dragged, over }) => {
              if (!over) return undefined;
              const overData = dragData(over.data.current);
              const crossing =
                onCross && overData
                  ? resolveRoomRailDragDestination(
                      dragData(dragged.data.current) ?? { kind: "" },
                      overData,
                    )
                  : null;
              const where =
                crossing !== null && crossing.kind !== "reorder" ? ` ${crossing.label}` : "";
              return `Over ${overData?.label ?? "item"}${where}`;
            },
            onDragEnd: () => "Reordering finished.",
            onDragCancel: () => "Reordering cancelled.",
          },
        }}
      >
        <div
          data-room-rail-dragging={active ? "true" : undefined}
          data-room-rail-drop={target?.id}
          data-room-rail-drop-after={target?.after ? "true" : "false"}
          data-room-rail-drag-destination={destination?.kind}
          className={cn("w-full min-w-0", contentClassName)}
        >
          {children}
          {/* No transforms on real rows: the selected border stays at its original placeholder. */}
          <style>{`[data-room-rail-drop] [data-room-reorder-over="true"] { outline: 1px dashed var(--border); outline-offset: 2px; }
      [data-room-rail-drop-after="false"] [data-room-reorder-over="true"]::before, [data-room-rail-drop-after="true"] [data-room-reorder-over="true"]::after { content: ''; position: absolute; left: 0; right: 0; height: 2px; background: var(--muted-foreground); pointer-events: none; }
      [data-room-rail-drop-after="false"] [data-room-reorder-over="true"]::before { top: -3px; }
      [data-room-rail-drop-after="true"] [data-room-reorder-over="true"]::after { bottom: -3px; }`}</style>
        </div>
        {mounted
          ? createPortal(
              <DragOverlay dropAnimation={null}>
                {active ? (
                  <div className="flex max-w-64 items-center gap-2 rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
                    <GripVertical className="size-3 shrink-0" />
                    <span className="truncate">{active.label}</span>
                    {cue !== null ? (
                      <span
                        className="shrink-0 rounded-sm border border-border bg-background px-1 text-[10px] text-muted-foreground"
                        data-room-rail-drag-cue={destination?.kind}
                      >
                        {cue}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </DragOverlay>,
              document.body,
            )
          : null}
      </DndContext>
    </div>
  );
}
/**
 * The unchanged children remain the selected-door geometry. The whole row
 * starts a drag (the pointer sensor's 6px activation distance is what tells
 * a drag from a click, and dnd-kit swallows the click that follows a drag);
 * the grip stays the visible handle, the keyboard step, and the menu.
 */
export function RoomRailReorderItem({
  item,
  children,
  enabled,
  onStep,
  canUp,
  canDown,
  sortControls,
  gripSide = "left",
}: {
  gripSide?: "left" | "right";
  item: RoomRailDragData;
  children: ReactNode;
  enabled: boolean;
  onStep: (direction: -1 | 1) => void;
  canUp: boolean;
  canDown: boolean;
  sortControls?: ReactNode;
}) {
  const key = `${item.kind}:${item.id}`;
  const draggable = useDraggable({ id: key, data: item, disabled: !enabled });
  const droppable = useDroppable({ id: key, data: item, disabled: !enabled });
  const [menu, setMenu] = useState(false);
  return (
    <div
      ref={(node) => {
        draggable.setNodeRef(node);
        droppable.setNodeRef(node);
      }}
      className="group/reorder relative w-full min-w-0"
      data-room-reorder={key}
      data-room-reorder-over={droppable.isOver && !draggable.isDragging ? "true" : undefined}
      {...(enabled ? draggable.listeners : {})}
    >
      {children}
      {enabled ? (
        <Popover open={menu} onOpenChange={setMenu}>
          <PopoverTrigger
            render={
              <button
                type="button"
                ref={draggable.setActivatorNodeRef}
                aria-label={`Arrange ${item.kind === "room" ? "Room" : item.kind === "pin" ? "pinned session" : item.kind === "launcher" ? "launcher" : item.kind === "context-view" ? "Context view" : "session"}: ${item.label}`}
                title="Drag to reorder; click for arrangement options"
                // DATED 2026-09-21: one 28px row anchored to the seat's top edge —
                // the dock's hit floor ([data-desk-dock] button, min-block-size
                // 28px) had grown the 24px grip while its 2px offset stayed, so
                // the last chip's grip hung 2px past its seat, the scroller
                // counted it, and the dock's edge hint fired under a 388px pool.
                // A positioned control inside a dock anchors to its seat's edges;
                // it never carries an offset plus a height of its own.
                className={cn(
                  "absolute top-0 flex h-7 cursor-grab items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-ring group-hover/reorder:opacity-70 group-focus-within/reorder:opacity-70",
                  gripSide === "left" ? "-left-3.5 w-3.5" : "-right-2.5 w-2.5",
                  draggable.isDragging && "cursor-grabbing",
                )}
                onKeyDown={(event) => {
                  if (event.altKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
                    event.preventDefault();
                    event.stopPropagation();
                    if (event.key === "ArrowUp" ? canUp : canDown)
                      onStep(event.key === "ArrowUp" ? -1 : 1);
                  }
                }}
              >
                <GripVertical className="size-3" aria-hidden="true" />
              </button>
            }
          />
          <PopoverPopup
            side={gripSide === "left" ? "right" : "left"}
            align="start"
            className="w-56"
            viewportClassName="flex flex-col gap-1 p-2"
          >
            <span className="truncate px-2 pb-1 text-xs font-medium">Arrange {item.label}</span>
            <button
              type="button"
              disabled={!canUp}
              className="rounded px-2 py-1.5 text-left text-xs hover:bg-accent disabled:opacity-40"
              onClick={() => {
                onStep(-1);
                setMenu(false);
              }}
            >
              Move up
            </button>
            <button
              type="button"
              disabled={!canDown}
              className="rounded px-2 py-1.5 text-left text-xs hover:bg-accent disabled:opacity-40"
              onClick={() => {
                onStep(1);
                setMenu(false);
              }}
            >
              Move down
            </button>
            {sortControls ? (
              <div className="border-t border-border px-2 pt-2">{sortControls}</div>
            ) : null}
          </PopoverPopup>
        </Popover>
      ) : null}
    </div>
  );
}
